import { GeoJsonLayerConfig, LegendLayerConfig, MapLayerConfig } from "../../../types";
import { isFulcrumDerivedLayerConfig } from "../../../utilities/fulcrumLibService";

// Format folder names to be more user-friendly - minimal version
export const getFolderName = (type: string): string => {
  switch (type) {
    case "fulcrum":
      return "Fulcrum";
    case "overlays":
      return "Overlays";
    case "raster-dem":
      return "Terrain";
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

/** Legend accordion key — Fulcrum is its own group; all other vector/raster layers share "overlays". */
const getLegendPanelGroupKey = (layer: LegendLayerConfig): string => {
  if (isFulcrumDerivedLayerConfig(layer)) return "fulcrum";
  return "overlays";
};

export const getGroupedLayersByType = (layers: MapLayerConfig[]): Record<string, LegendLayerConfig[]> => {
  const legendLayers = layers
    .filter((layer): layer is LegendLayerConfig => {
      return (layer.type === "geojson" || layer.type === "raster" || layer.type === "fulcrum") && !layer.error;
    })
    .reverse(); // reverse the list so that the order is same as drawing order on map

  // Group by legend panel category (not raw layer.type — Fulcrum is converted to geojson in store)
  const groupedLayers: Record<string, LegendLayerConfig[]> = legendLayers.reduce(
    (acc, layer) => {
      const type = getLegendPanelGroupKey(layer);
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(layer);
      return acc;
    },
    {} as Record<string, LegendLayerConfig[]>
  );

  return groupedLayers;
};

// Define the order of legend groups with Fulcrum first
const layerTypeOrder = ["fulcrum", "overlays", "raster-dem"];

export const getSortedGroups = (groups: string[]): string[] => {
  // Sort the layer types according to our defined order
  return groups.sort((a, b) => {
    const indexA = layerTypeOrder.indexOf(a);
    const indexB = layerTypeOrder.indexOf(b);
    // If a type isn't in our order array, put it at the end
    const posA = indexA === -1 ? 999 : indexA;
    const posB = indexB === -1 ? 999 : indexB;
    return posA - posB;
  });
};

// Check if layer has multiple icons based on a match expression
export const checkIfHasMultipleIcons = (layer: LegendLayerConfig) => {
  if (
    layer.type === "geojson" &&
    "style" in layer &&
    layer.style &&
    "iconStyle" in layer.style &&
    Array.isArray(layer.style.iconStyle) &&
    layer.style.iconStyle[0] === "match"
  ) {
    return true;
  }
  return false;
};

// Extract icon mappings from a match expression
export const getIconMappings = (layer: GeoJsonLayerConfig) => {
  if (!checkIfHasMultipleIcons(layer)) return [];

  // Cast to GeoJsonLayerConfig to access style safely after checking in hasMultipleIcons
  const geoJsonLayer = layer as GeoJsonLayerConfig;
  const iconStyle = geoJsonLayer.style.iconStyle as any[];
  const mappings = [];

  if (iconStyle[0] === "match") {
    // Check if this is a modulo match: ["match", ["%", ["get", "id"], 7], ...]
    const isModuloMatch = Array.isArray(iconStyle[1]) && iconStyle[1][0] === "%";

    if (isModuloMatch) {
      // Modulo match format: ["match", ["%", ["get", "id"], 7], 0, "icon1", 1, "icon2", ...]
      for (let i = 2; i < iconStyle.length - 1; i += 2) {
        const value = iconStyle[i];
        const icon = iconStyle[i + 1];

        // Create user-friendly labels based on icon type
        let friendlyLabel = `Sites ${value + 1}`;
        if (icon.includes("Camp")) friendlyLabel = "Camp Sites";
        else if (icon.includes("Berries")) friendlyLabel = "Berries/Food Sites";
        else if (icon.includes("Dugong")) friendlyLabel = "Marine/Water Sites";
        else if (icon.includes("Stone")) friendlyLabel = "Stone/Artifact Sites";
        else if (icon.includes("Spear")) friendlyLabel = "Tool/Weapon Sites";
        else if (icon.includes("Waterhole")) friendlyLabel = "Water Sources";
        else if (icon.includes("Turtle")) friendlyLabel = "Sacred/Ceremony Sites";

        mappings.push({ value: friendlyLabel, icon });
      }
    } else {
      // Standard match format: ["match", ["get", "property"], "value1", "icon1", ...]
      const property = iconStyle[1][1]; // The property being matched (e.g., "type")

      // Start at index 2 and process pairs until the last item (default)
      for (let i = 2; i < iconStyle.length - 1; i += 2) {
        const value = iconStyle[i];
        const icon = iconStyle[i + 1];

        // Handle arrays of values that map to the same icon
        if (Array.isArray(value)) {
          value.forEach((v) => {
            mappings.push({ value: v, icon });
          });
        } else {
          mappings.push({ value, icon });
        }
      }
    }
  }

  return mappings;
};
